import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getRentioPayUrl } from "@rentiohq/shared-frontend/dist/utils/url.utils";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { NavigateFunction } from "react-router-dom";
import parse from "url-parse";

export enum EDeeplinkScope {
  Activity = "activity",
  AddCharacteristic = "add_characteristic",
  Chat = "chat",
  ChatNew = "chat_new",
  Contact = "contact",
  ContactNew = "contact_new",
  ContractNew = "contract_new",
  Contracts = "contracts",
  Document = "document",
  ForcedUpdate = "forced_update",
  IndexContract = "index_contract",
  InternalMenu = "internal_menu",
  KnowYourCustomer = "know_your_customer",
  Payment = "payment",
  PaymentOrder = "payment_order",
  PaymentOrderNew = "payment_order_new",
  PaymentOrders = "payment_orders",
  PaymentRequest = "payment_request",
  PayoutRentDeposit = "payout_rentdeposit",
  ProblemReport = "problem_report",
  Profile = "profile",
  Properties = "properties",
  Property = "property",
  PropertyCreate = "property_create",
  Task = "task",
  Tasks = "tasks",
  TemplateDocument = "template-document",
  TemplateDocumentExternal = "template-document-external",
  ValidateRentDeposit = "validate_rentdeposit",
  AskRemainingData = "AskRemainingData",
}

const showUnsupportedDeeplinkAlert = (isTenantDashboard?: boolean) => {
  if (isTenantDashboard) {
    enqueueSnackbar({
      message: getLocalizedText("card.alert.action_not_supported.dashboard"),
      variant: "info",
    });
    return;
  }

  showAlert({
    type: "info",
    message: getLocalizedText("card.alert.action_not_supported.dashboard"),
  });
};

export const openDeeplink = (params: {
  url: string;
  navigate: NavigateFunction;
  setQueryParamValue: (params: { [key: string]: any }) => void;
  isTenantDashboard?: boolean;
}) => {
  const { navigate, setQueryParamValue, isTenantDashboard } = params;
  let { url } = params;

  if (
    url.startsWith("https://link.rentio.be") ||
    url.startsWith("https://link.rentio-internal.be")
  ) {
    const linkUrl = new URL(url);
    const urlFromLinkUrl = linkUrl.searchParams.get("link");
    if (urlFromLinkUrl) {
      url = urlFromLinkUrl;
    }
  }

  const parsedUrl = parse(url, true);
  const { pathname, hostname, query = {} } = parsedUrl;
  const pathComponents = (
    pathname.startsWith("/") ? pathname.substr(1) : pathname
  ).split("/");
  const scope = (pathComponents[0] as EDeeplinkScope) || query.type;

  if (!hostname.includes("rentio") && pathComponents.length === 0) {
    return;
  }

  const { id } = query;
  switch (scope) {
    case EDeeplinkScope.Tasks:
    case EDeeplinkScope.Task:
      if (id) {
        navigate(`/tasks/${id}`);
      }
      break;

    case EDeeplinkScope.IndexContract:
      navigate(
        `/follow-up/indexable-contracts?stage=indexable&contractId=${query.contractId}`,
      );
      break;

    case EDeeplinkScope.Document:
      if (id) {
        setQueryParamValue({
          documentId: id,
        });
        break;
      }

      showUnsupportedDeeplinkAlert();
      break;

    case EDeeplinkScope.Payment:
      if (id) {
        isTenantDashboard
          ? navigate(`/payment/${id}`)
          : window.open(
              getRentioPayUrl({
                path: `payment/${id}`,
              }),
              "_blank",
            );
        break;
      }

      showUnsupportedDeeplinkAlert();
      break;

    case EDeeplinkScope.AskRemainingData:
      if (isTenantDashboard) {
        setQueryParamValue({
          type: EDeeplinkScope.AskRemainingData,
          contactId: query?.contactId,
          externalActionId: query?.externalActionId,
          externalActionKey: query?.externalActionKey,
          token: query?.token,
        });
        break;
      }

      showUnsupportedDeeplinkAlert(isTenantDashboard);

      break;

    case EDeeplinkScope.KnowYourCustomer:
      isTenantDashboard ? navigate("/kyc") : showUnsupportedDeeplinkAlert();
      break;

    case EDeeplinkScope.PayoutRentDeposit: {
      if (!isTenantDashboard) {
        return showUnsupportedDeeplinkAlert();
      }

      const {
        contractId,
        select_bank_account = "-1",
        amount,
        kyc,
        identityValidation,
        identityValidationKey,
        rentDepositId,
      } = query;

      const selectBankAccount = Boolean(parseInt(select_bank_account));
      const kycRequired = !!parseInt(kyc || "0", 0);
      if (selectBankAccount) {
        navigate("/bank-account", {
          state: {
            contractId,
            amount: parseInt(amount || "0", 0),
            kycRequired,
            identityValidation: !!parseInt(query.identityValidation || "0", 0),
            identityValidationKey,
            rentDepositId,
          },
        });
      } else {
        let identityValidationUrl;
        if (identityValidation && rentDepositId && identityValidationKey) {
          identityValidationUrl = `/rent-deposits/${rentDepositId}/validations/${identityValidationKey}`;
        }
        navigate("/kyc", {
          state: { identityValidationUrl, kycRequired },
        });
      }

      break;
    }

    case EDeeplinkScope.ValidateRentDeposit: {
      if (!isTenantDashboard) {
        return showUnsupportedDeeplinkAlert();
      }

      const { identityValidationKey = "", rentDepositId } = query;

      const identityValidationUrl = `/rent-deposits/${rentDepositId}/validations/${identityValidationKey}`;

      navigate("/kyc", {
        state: { identityValidationUrl, kycRequired: false },
      });
      break;
    }

    default:
      showUnsupportedDeeplinkAlert(isTenantDashboard);
      break;
  }
};
